import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchData } from '../../../helpers/fetchData'
import CustomButton from '../../../shared/CustomButton'
import { ProgressBar } from 'primereact/progressbar'
import moment from 'moment'
import ConfirmationModal from '../../../shared/ConfirmationModal'
import { formatDate } from '../../../utils/FormateDateTime'
import CheckPermission from '../../../hooks/CheckPermission'

function BulkProcessingLogs({ jobId, onClose, onComplete, onStatusChange }) {
  const { t } = useTranslation()
  const { hasPermission } = CheckPermission()
  const [jobStatus, setJobStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [expandedFailures, setExpandedFailures] = useState(false)
  const [showStopConfirm, setShowStopConfirm] = useState(false)
  const pollingInterval = useRef(null)

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current)
      pollingInterval.current = null
    }
  }

  const startPolling = () => {
    stopPolling()
    pollingInterval.current = setInterval(() => {
      fetchJobStatus()
    }, 35000)
  }

  const fetchJobStatus = async () => {
    if (!jobId) return

    try {
      setLoading(true)
      const response = await fetchData('GET', `/v3/invoices/bulkJobStatus/${jobId}`)
      setLoading(false)
      if (response) {
        setJobStatus(response)
        onStatusChange?.(response)

        if (['completed', 'completed_with_errors', 'failed', 'stopped'].includes(response.status)) {
          if (['completed', 'completed_with_errors', 'failed'].includes(response.status)) {
            onComplete?.(response)
          }
          stopPolling()
        }
      }
    } catch (error) {
      setLoading(false)
      console.error('Failed to fetch job status:', error)
    }
  }

  const confirmStopJob = () => {
    setShowStopConfirm(true)
  }

  const handleStopJob = async () => {
    if (!jobId || !hasPermission('cliamgeneration', 'update')) return

    try {
      setActionLoading(true)
      const response = await fetchData('POST', `/v3/invoices/bulkJobStop/${jobId}`)
      if (response) {
        setShowStopConfirm(false)
        stopPolling()
        await fetchJobStatus()
      }
    } catch (error) {
      console.error('Failed to stop job:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleResumeJob = async () => {
    if (!jobId || !hasPermission('cliamgeneration', 'update')) return

    try {
      setActionLoading(true)
      const response = await fetchData('POST', `/v3/invoices/bulkJobResume/${jobId}`)
      if (response) {
        startPolling()
        await fetchJobStatus()
      }
    } catch (error) {
      console.error('Failed to resume job:', error)
    } finally {
      setActionLoading(false)
    }
  }

  useEffect(() => {
    if (jobId) {
      setLoading(true)
      fetchJobStatus().finally(() => setLoading(false))
      startPolling()
    }

    return () => {
      stopPolling()
    }
  }, [jobId])

  const getStatusBadge = status => {
    const statusConfig = {
      queued: { label: 'Queued', className: 'bg-gray-500' },
      processing: { label: 'Processing', className: 'bg-blue-500' },
      stopped: { label: 'Stopped', className: 'bg-orange-500' },
      completed: { label: 'Completed', className: 'bg-green-500' },
      completed_with_errors: { label: 'Completed with Errors', className: 'bg-yellow-500' },
      failed: { label: 'Failed', className: 'bg-red-500' }
    }

    const config = statusConfig[status] || statusConfig.queued
    return (
      <span className={`px-3 py-1 rounded-full text-white text-sm ${config.className}`}>
        {t(config.label)}
      </span>
    )
  }

  if (loading && !jobStatus) {
    return (
      <div className='p-4 text-center'>
        <i className='pi pi-spin pi-spinner text-2xl text-primary'></i>
      </div>
    )
  }

  if (!jobStatus) {
    return (
      <div className='p-4 text-center text-gray-500'>
        {t('No job status available')}
      </div>
    )
  }

  const canStop = hasPermission('cliamgeneration', 'update') && ['queued', 'processing'].includes(jobStatus.status)
  const canResume = hasPermission('cliamgeneration', 'update') && jobStatus.status === 'stopped'

  return (
    <>
      <div className='space-y-4'>
        <div className='flex justify-between items-center'>
          <div>
            <h3 className='text-lg font-semibold'>{t('Batch Status')}</h3>
          </div>
          {getStatusBadge(jobStatus.status)}
        </div>
        <div>
          <div className='flex justify-between text-sm mb-1'>
            <span>{t('Progress')}</span>
            <span>{jobStatus.progress_percentage?.toFixed(1)}%</span>
          </div>
          <ProgressBar
            value={jobStatus.progress_percentage || 0}
            showValue={false}
          />
        </div>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          <div className='bg-blue/10 p-2 rounded-lg'>
            <p className='text-xs text-gray-600'>{t('Total')}</p>
            <p className='text-xl font-bold text-blue/60'>
              {jobStatus.total_visits || jobStatus.total_claims || 0}
            </p>
          </div>
          <div className='bg-gray-100 p-2 rounded-lg'>
            <p className='text-xs text-gray-600'>{t('Processed')}</p>
            <p className='text-xl font-bold text-gray-600'>
              {jobStatus.processed_count || 0}
            </p>
          </div>
          <div className='bg-green-50 p-2 rounded-lg'>
            <p className='text-xs text-gray-600'>{t('Success')}</p>
            <p className='text-xl font-bold text-green-600'>
              {jobStatus.success_count || 0}
            </p>
          </div>
          <div className='bg-red-50 p-2 rounded-lg'>
            <p className='text-xs text-gray-600'>{t('Failed')}</p>
            <p className='text-xl font-bold text-red-600'>
              {jobStatus.failed_count || 0}
            </p>
          </div>
        </div>
        {jobStatus.failures && jobStatus.failures.length > 0 && (
          <div className='border border-red-200 rounded-lg overflow-hidden'>
            <div
              className='bg-red-50 p-3 flex justify-between items-center cursor-pointer'
              onClick={() => setExpandedFailures(!expandedFailures)}
            >
              <span className='font-medium text-red-700'>
                {t('Failures')} ({jobStatus.failures.length})
              </span>
            </div>

            {expandedFailures && (
              <div className='max-h-64 overflow-y-auto'>
                <table className='w-full text-sm'>
                  <thead className='bg-gray-100 sticky top-0'>
                    <tr>
                     <th className='p-2 text-left'>{t('Visit Date')}</th>
                      <th className='p-2 text-left'>{t('Patient Name')}</th>
                      <th className='p-2 text-left'>{t('MRN')}</th>
                      <th className='p-2 text-left'>{t('Error')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobStatus.failures.map((failure, index) => (
                      <tr key={index} className='border-t'>
                        <td className='p-2'>{failure.visit_date ? formatDate(failure.visit_date) : '-'}</td>
                        <td className='p-2'>{failure.patient_name || '-'}</td>
                        <td className='p-2'>{failure?.file_number || '-'}</td>
                        <td className='p-2 text-red-600'>{failure.error_message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        <div className='flex gap-2 justify-end pt-2'>
          {canResume && (
            <CustomButton
              label={t('Resume')}
              icon='pi pi-play'
              buttonType='primary'
              onClick={handleResumeJob}
              disabled={actionLoading}
              loading={actionLoading}
            />
          )}
          {canStop && (
            <CustomButton
              label={t('Stop')}
              icon='pi pi-stop'
              buttonType='danger'
              onClick={confirmStopJob}
              disabled={actionLoading}
              loading={actionLoading}
            />
          )}
          <CustomButton
            label={t('Refresh')}
            icon='pi pi-refresh'
            buttonType='secondary'
            onClick={fetchJobStatus}
            disabled={loading || actionLoading}
          />
        </div>
      </div>

      <ConfirmationModal
        visible={showStopConfirm}
        setVisible={setShowStopConfirm}
        onAccept={handleStopJob}
        header={t('Stop Job')}
        message={t('Are you sure you want to stop this job?')}
        acceptLabel={t('Stop')}
        rejectLabel={t('Cancel')}
        isLoading={actionLoading}
        onReject={() => setShowStopConfirm(false)}
      />
    </>
  )
}

export default BulkProcessingLogs

